/**
 * Landmark card effect scoring — §7.5 + §9.5.
 *
 * `applyLandmarkEffects` takes the base breakdown (already computed by
 * corridor/wildlife/majority/nature) and applies each landmark card the
 * player has picked. Effects that modify corridor/majority inputs
 * (singleTileCorridorCounts, minTwoTileCorridor, keystoneCountsAsHabitat)
 * are plumbed through the corridor phase opts — here they are no-ops
 * (the corridor phase already consumed them). Direct-point effects
 * (flatBonus, addToCorridor, addToMajority, pointsPerAnimalOnHabitat,
 * cappedBonus, ksCustom) modify the breakdown in place.
 *
 * Pure — no I/O, no mutations.
 */

import { TILE_DEFS_BY_ID } from "../data/tile-defs";
import type { Habitat } from "../types/habitat";
import type { LandmarkCardDef, LandmarkEffect } from "../types/landmark";
import type { PlayerScoreBreakdown, PlayerState } from "../types/player";
import type { Wildlife } from "../types/wildlife";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Returns true if the tile with `tileDefId` contains `habitat` on at least
 * one edge (§3.1 "A tile contains a habitat if any of its 6 edges has it").
 */
const tileContainsHabitat = (tileDefId: string, habitat: Habitat): boolean => {
  const def = TILE_DEFS_BY_ID[tileDefId];
  if (def === undefined) {
    return false;
  }
  return def.edges.some((e) => e === habitat);
};

/**
 * Counts tiles in `player.board` that contain `habitat` and carry `wildlife`.
 * Used by the `pointsPerAnimalOnHabitat` effect.
 */
const countWildlifeOnHabitatTiles = (
  player: PlayerState,
  habitat: Habitat,
  wildlife: Wildlife,
): number => {
  let count = 0;
  for (const cell of Object.values(player.board.cells)) {
    if (
      cell.wildlife === wildlife &&
      tileContainsHabitat(cell.tileDefId, habitat)
    ) {
      count++;
    }
  }
  return count;
};

// ---------------------------------------------------------------------------
// KS custom landmark promo registry
// ---------------------------------------------------------------------------

/**
 * Registry of named KS promo landmark scoring functions.
 * Unregistered promoIds return 0.
 */
const KS_LANDMARK_REGISTRY: Readonly<
  Record<string, (player: PlayerState) => number>
> = {};

// ---------------------------------------------------------------------------
// recomputeTotal — always derive total from parts
// ---------------------------------------------------------------------------

const recomputeTotal = (bd: PlayerScoreBreakdown): number =>
  Object.values(bd.wildlifeByCard).reduce((a, b) => a + b, 0) +
  Object.values(bd.habitatByType).reduce((a, b) => a + b, 0) +
  bd.habitatMajorityBonus +
  bd.nature +
  bd.landmarks;

// ---------------------------------------------------------------------------
// applyOneLandmarkEffect — pure application of one effect
// ---------------------------------------------------------------------------

const applyOneLandmarkEffect = (
  player: PlayerState,
  breakdown: PlayerScoreBreakdown,
  effect: LandmarkEffect,
): PlayerScoreBreakdown => {
  switch (effect.kind) {
    case "flatBonus": {
      const landmarks = breakdown.landmarks + effect.points;
      const updated = { ...breakdown, landmarks };
      return { ...updated, total: recomputeTotal(updated) };
    }

    case "addToCorridor": {
      // Per-tile bonus on the largest corridor of the habitat.
      // The corridor size is recorded in habitatByType[habitat].
      const corridorSize = breakdown.habitatByType[effect.habitat] ?? 0;
      const bonus = corridorSize * effect.pointsPerTileInLargest;
      const landmarks = breakdown.landmarks + bonus;
      const updated = { ...breakdown, landmarks };
      return { ...updated, total: recomputeTotal(updated) };
    }

    case "addToMajority": {
      // Adds extra weight to the majority bonus for the given habitat.
      // The actual majority bonus was pre-computed; we add to it directly.
      const habitatMajorityBonus =
        breakdown.habitatMajorityBonus + effect.bonus;
      const updated = { ...breakdown, habitatMajorityBonus };
      return { ...updated, total: recomputeTotal(updated) };
    }

    case "pointsPerAnimalOnHabitat": {
      const count = countWildlifeOnHabitatTiles(
        player,
        effect.habitat,
        effect.wildlife,
      );
      const bonus = count * effect.pointsPerAnimal;
      const landmarks = breakdown.landmarks + bonus;
      const updated = { ...breakdown, landmarks };
      return { ...updated, total: recomputeTotal(updated) };
    }

    case "cappedBonus": {
      // perTile × corridorSize, capped at capPoints, floor 0.
      const corridorSize = breakdown.habitatByType[effect.habitat] ?? 0;
      const raw = corridorSize * effect.perTile;
      const bonus = Math.min(Math.max(raw, effect.floor), effect.capPoints);
      const landmarks = breakdown.landmarks + bonus;
      const updated = { ...breakdown, landmarks };
      return { ...updated, total: recomputeTotal(updated) };
    }

    case "singleTileCorridorCounts":
    case "minTwoTileCorridor":
    case "keystoneCountsAsHabitat":
      // These are corridor-phase modifiers — already consumed before this
      // function is called. No post-hoc point adjustment needed.
      return breakdown;

    case "ksCustom": {
      const fn = KS_LANDMARK_REGISTRY[effect.promoId];
      if (fn === undefined) {
        return breakdown;
      }
      const bonus = fn(player);
      const landmarks = breakdown.landmarks + bonus;
      const updated = { ...breakdown, landmarks };
      return { ...updated, total: recomputeTotal(updated) };
    }

    default: {
      const _exhaustive: never = effect;
      return _exhaustive;
    }
  }
};

// ---------------------------------------------------------------------------
// applyLandmarkEffects — public API
// ---------------------------------------------------------------------------

/**
 * Applies all landmark card effects for the player's picked cards.
 *
 * Only cards whose `cardId` appears in `player.landmarkPicks` are applied.
 * `landmarkCards` is the full catalog to look up effects from.
 *
 * Returns a new `PlayerScoreBreakdown` with `landmarks` and `total` updated.
 * Input `baseBreakdown` is not mutated.
 */
export const applyLandmarkEffects = (
  player: PlayerState,
  baseBreakdown: PlayerScoreBreakdown,
  landmarkCards: readonly LandmarkCardDef[],
): PlayerScoreBreakdown => {
  const pickSet = new Set(player.landmarkPicks);
  const applicableCards = landmarkCards.filter((c) => pickSet.has(c.cardId));

  let breakdown = baseBreakdown;
  for (const card of applicableCards) {
    breakdown = applyOneLandmarkEffect(player, breakdown, card.effect);
  }
  return breakdown;
};
